import { useMemo } from 'react';

import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { CodeBlock } from '@components/code-block';
import { createForm, useCreateFormStore } from '@components/form';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCondition } from '@components/icons/condition';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconFile } from '@components/icons/file';
import { IconInfo } from '@components/icons/info';
import { IconInput } from '@components/icons/input';
import { IconList } from '@components/icons/list';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPlay } from '@components/icons/play';
import { IconRemove } from '@components/icons/remove';
import { IconWebhook } from '@components/icons/webhook';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { MenuItem } from '@components/menu/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { Font } from '@styles/theme';
import { sanitizeJSON } from '@utils/collections';

import {
  ClampedText,
  CUSTOM_FIELD_PREFIX,
  DraggableIcon,
  FieldTable,
  MethodTag,
  getFormEndpointVariables,
  getVariableCount,
  methodFancyContainerColorMap,
} from './common';
import { useCustomValues } from './custom-values-section';
import {
  useIntegrationWebhookLogs,
  useResendIntegrationWebhookLog,
  useUpdateIntegrationWebhook,
} from './integrations-hooks';
import { useIntegrationsApi } from './integrations-provider';
import type {
  ConditionComparator,
  IntegrationEndpointVariable,
  IntegrationWebhook,
  WebhookCondition,
} from './integrations-types';
import { useSubscribeWebhookModal } from './modals/create-webhook';
import { useDeleteWebhookModal } from './modals/delete-webhook';
import { useDuplicateIntegrationWebhookModal } from './modals/duplicate-webhook';
import { useUnsubscribeWebhookModal } from './modals/remove-webhook';
import { useIntegrationWebhookCreateSidebar } from './sidebars/add-edit-webhook';
import { useIntegrationWebhookConditionsSidebar } from './sidebars/add-webhook-condition';
import { UsageCard } from './usage-card';
import { WebhookLogList } from './webhook-log-list';

let getComparatorLabelHelper: ((comparator: ConditionComparator) => string) | undefined;
let getValueLabelHelper: ((value: any, comparator?: string) => string) | undefined;

export const setWebhookLabelHelpers = (helpers: {
  getComparatorLabel?: (comparator: ConditionComparator) => string;
  getValueLabel?: (value: any, comparator?: string) => string;
}) => {
  if (helpers.getComparatorLabel) {
    getComparatorLabelHelper = helpers.getComparatorLabel;
  }
  if (helpers.getValueLabel) {
    getValueLabelHelper = helpers.getValueLabel;
  }
};

export type WebhookCardProps = {
  webhook: IntegrationWebhook;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
};

export const WebhookCard = ({ webhook, dragAttributes, dragListeners }: WebhookCardProps) => {
  const { t } = useScopedTranslate(locale);
  const showDeleteModal = useDeleteWebhookModal();
  const showEditSidebar = useIntegrationWebhookCreateSidebar();
  const showDuplicateModal = useDuplicateIntegrationWebhookModal();
  const integrationId = webhook.integration;

  const handleEdit = () => {
    showEditSidebar({ integrationId, webhook });
  };

  const handleDuplicate = () => {
    showDuplicateModal({ webhook });
  };

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      space={4}
      variant="default"
      key={webhook.shortId}
      renderCollapse={() => (
        <Stack space={2}>
          {webhook.isSubscribable && <Details webhook={webhook} />}
          <PayloadValues webhook={webhook} />
          {webhook.webhookCreated && (
            <>
              {getComparatorLabelHelper && getValueLabelHelper && (
                <ConditionValues
                  webhook={webhook}
                  getComparatorLabel={getComparatorLabelHelper}
                  getValueLabel={getValueLabelHelper}
                />
              )}
              <Logs webhook={webhook} />
            </>
          )}
          <UsageCard
            itemType="webhook"
            shortId={webhook.shortId}
            numJourneys={webhook.numJourneys}
          />
        </Stack>
      )}
    >
      <Stack>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack space={4} orientation="horizontal" align="center">
            <DraggableIcon
              variant={methodFancyContainerColorMap[webhook.method]}
              icon={<IconWebhook />}
              dragAttributes={dragAttributes}
              dragListeners={dragListeners}
            />
            <div>
              <ClampedText text={webhook.name} font="semibold14" />
              {webhook.description && (
                <ClampedText text={webhook.description} font="regular14" color="textSecondary" />
              )}
              <ClampedText text={webhook.uniqueUrl} font="regular12" color="textHelper" />
            </div>
          </Stack>
          <Stack space={4} orientation="horizontal" align="center">
            <MethodTag method={webhook.method} />
            <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}>
              <MenuItem icon={<IconEdit />} label={t('core.edit')} onSelect={handleEdit} />
              <MenuItem
                icon={<IconDuplicate />}
                label={t('core.duplicate')}
                onSelect={handleDuplicate}
              />
              <Menu.Separator />
              <MenuItem
                icon={<IconRemove />}
                label={t('removeWebhook')}
                variant="danger"
                onSelect={() => showDeleteModal({ webhook })}
              />
            </Menu>
          </Stack>
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const Details = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const showSubscribeModal = useSubscribeWebhookModal();

  const handleClickSubscribe = () => {
    showSubscribeModal({ webhookId: webhook.shortId });
  };

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed={webhook.webhookCreated}
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() =>
        webhook.webhookCreated ? (
          <CreatedDetails webhook={webhook} />
        ) : (
          <Stack orientation="horizontal" align="center" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <IconWebhook color="elementPurple80" />
              <Text text={t('webhookNotSubscribed')} font="semibold14" color="textHelper" />
            </Stack>
            <Button
              stopPropagation
              variant="secondary"
              text={t('subscribe')}
              iconLeft={<IconAddPlus />}
              onClick={handleClickSubscribe}
            />
          </Stack>
        )
      }
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack orientation="horizontal" align="center" space={2}>
          <IconInfo color="elementPurple50" />
          <Text text={t('details')} font="semibold14" />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const CreatedDetails = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const showUnsubscribeModal = useUnsubscribeWebhookModal();

  const handleUnsubscribe = () => {
    showUnsubscribeModal({ webhook });
  };

  return (
    <Stack space={4}>
      <Stack space={4} orientation="horizontal">
        <CodeBlockContainer>
          <CodeBlock header={t('requestValues')} data={webhook.requestData} />
        </CodeBlockContainer>
        <CodeBlockContainer>
          <CodeBlock header={t('responseValues')} data={webhook.responseValues} />
        </CodeBlockContainer>
      </Stack>
      <div>
        <Button
          stopPropagation
          variant="secondary"
          text={t('unsubscribeWebhook')}
          iconLeft={<IconRemove />}
          onClick={handleUnsubscribe}
        />
      </div>
    </Stack>
  );
};

const CodeBlockContainer = styled.div({
  flex: 1,
  overflow: 'auto',
});

type PayloadData = {};

const PayloadForm = createForm<PayloadData>();

const PayloadValues = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const payLoadVariables = webhook.payloadVariables as IntegrationEndpointVariable[];
  const standardVariables = payLoadVariables.filter(
    (variable) => !variable.name.startsWith(CUSTOM_FIELD_PREFIX)
  );
  const customVariables = payLoadVariables.filter((variable) =>
    variable.name.startsWith(CUSTOM_FIELD_PREFIX)
  );
  // payloadData comes in as a JSON string, so parse it for the jq editor.
  const exampleData = useMemo(() => {
    let value: unknown = webhook.payloadData;
    if (typeof webhook.payloadData === 'string') {
      try {
        value = JSON.parse(webhook.payloadData);
      } catch {
        value = null;
      }
    }
    const sanitized = sanitizeJSON(value);
    return sanitized && typeof sanitized === 'object' ? sanitized : null;
  }, [webhook.payloadData]);
  const formActionPortalId = `${webhook.shortId}-payload-form-actions-portal`;
  const updateMutation = useUpdateIntegrationWebhook();
  const showEditSidebar = useIntegrationWebhookCreateSidebar();
  const integrationId = webhook.integration;

  const handleEdit = () => {
    showEditSidebar({ integrationId, webhook });
  };

  const visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    required: false,
    format: false,
  };

  const handleUpdateVariables = async () => {
    const variables = getFormEndpointVariables({
      formStore,
      endpointVariables: standardVariables,
      visibleColumns,
    });
    await updateMutation.mutateAsync({
      shortId: webhook.shortId,
      data: {
        payloadVariables: [...variables, ...customVariables],
      },
    });
    formStore.actions.markAsClean();
  };

  const handleSaveCustomValues = async (values: IntegrationEndpointVariable[]) => {
    await updateMutation.mutateAsync({
      shortId: webhook.shortId,
      data: {
        payloadVariables: values,
      },
    });
  };

  const { section: customValuesSection, sidebar: customValuesSidebar } = useCustomValues({
    values: payLoadVariables,
    exampleData,
    isSaving: updateMutation.isLoading,
    onSave: handleSaveCustomValues,
    dataTitle: t('payloadData'),
    dataDescription: t('payloadQueryInfo'),
  });

  return (
    <>
      <CollapseCard
        keepCollapsedInDom
        defaultCollapsed
        spaceX={4}
        spaceY={2}
        variant="gray"
        renderCollapse={() => (
          <Stack space={4}>
            <PayloadForm store={formStore} onSubmit={handleUpdateVariables}>
              {standardVariables.length > 0 ? (
                <FieldTable
                  endpointId={webhook.shortId}
                  form={PayloadForm}
                  endpointVariables={standardVariables}
                  visibleColumns={visibleColumns}
                  showValueType={false}
                />
              ) : (
                <Stack orientation="horizontal" align="center" justify="space-between">
                  <Stack space={2} orientation="horizontal" align="center">
                    <IconInput color="elementPurple80" />
                    <Text text={t('noPayloadValues')} font="semibold14" color="textSecondary" />
                  </Stack>
                  <Button
                    stopPropagation
                    variant="secondary"
                    text={t('editPayload')}
                    iconLeft={<IconAddPlus />}
                    onClick={handleEdit}
                  />
                </Stack>
              )}

              {isDirty && (
                <PayloadForm.Actions portal={formActionPortalId}>
                  <Stack orientation="horizontal" space={2} align="center">
                    <PayloadForm.Reset
                      stopPropagation
                      text={t('core.cancel')}
                      variant="secondary"
                    />
                    <PayloadForm.Submit
                      stopPropagation
                      isDisabled={updateMutation.isLoading}
                      text={t('core.save')}
                      variant="primary-blue"
                    />
                  </Stack>
                </PayloadForm.Actions>
              )}
            </PayloadForm>
            <Separator />
            {customValuesSection}
          </Stack>
        )}
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconInput color="elementPurple50" />
            <Text text={t('payloadValues')} font="semibold14" />
            <Text text="•" color="textHelper" />
            <Text text={getVariableCount(payLoadVariables)} font="semibold14" />
          </Stack>
          <Stack space={2} orientation="horizontal" align="center">
            <div id={formActionPortalId} />
          </Stack>
        </Stack>
      </CollapseCard>
      {customValuesSidebar}
    </>
  );
};

const ConditionValues = ({
  webhook,
  getComparatorLabel,
  getValueLabel,
}: {
  webhook: IntegrationWebhook;
  getComparatorLabel: (comparator: ConditionComparator) => string;
  getValueLabel: (value: any, comparator?: string) => string;
}) => {
  const { t } = useScopedTranslate(locale);
  const conditions = webhook.conditions ?? [];
  const showConditionsSidebar = useIntegrationWebhookConditionsSidebar();

  const handleEditConditions = () => {
    showConditionsSidebar({ webhookId: webhook.shortId });
  };

  const comparatorElements = conditions.map((condition: WebhookCondition, index: number) => {
    const { field, comparator, value } = condition;
    const valueLabel = getValueLabel(value as string, comparator);
    const fieldLabel = field?.variables && Object.values(field.variables)[0].label;
    if (!fieldLabel || !comparator) {
      return '';
    }
    const comparatorString = getComparatorLabel(comparator) || comparator;
    const commonProps = { font: 'regular14' as Font, style: { display: 'inline' } };
    const emptyValueProps = !valueLabel ? { font: 'semibold14' as Font } : {};
    const conjunction = t('core.and');
    return (
      <div key={index}>
        {styledTranslate({
          key: 'conditionDescription',
          variables: {
            conjunction: {
              text: index > 0 ? `${conjunction} ` : '',
              color: 'textSecondary',
              capitalize: true,
              ...commonProps,
            },
            field: {
              text: fieldLabel,
              color: 'elementBlue100',
              ...commonProps,
            },
            comparator: {
              text: comparatorString,
              color: 'textSecondary',
              ...commonProps,
            },
            value: {
              text: valueLabel || t('filledOutInTheJourney'),
              color: 'textSecondary',
              ...commonProps,
              ...emptyValueProps,
            },
          },
          translations: locale,
        })}
      </div>
    );
  });

  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <>
          {conditions.length === 0 ? (
            <Stack orientation="horizontal" align="center" justify="space-between">
              <Stack space={2} orientation="horizontal" align="center">
                <IconCondition color="elementPurple80" />
                <Text text={t('noConditions')} font="semibold14" color="textSecondary" />
              </Stack>
              <Button
                stopPropagation
                variant="secondary"
                text={t('addConditions')}
                iconLeft={<IconAddPlus />}
                onClick={handleEditConditions}
              />
            </Stack>
          ) : (
            <Stack space={4}>
              {comparatorElements}
              <div>
                <Button
                  stopPropagation
                  variant="secondary"
                  text={t('updateConditions')}
                  iconLeft={<IconCondition />}
                  onClick={handleEditConditions}
                />
              </div>
            </Stack>
          )}
        </>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={2} orientation="horizontal" align="center">
          <IconPlay color="elementPurple50" />
          <Text text={t('continueWhen')} font="semibold14" />
          <Text text="•" color="textHelper" />
          <Text text={conditions.length} font="semibold14" />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const Logs = ({ webhook }: { webhook: IntegrationWebhook }) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading } = useIntegrationWebhookLogs(webhook.shortId);
  const { resendIntegrationWebhookLog, getWebhookLogTravellerHref } = useIntegrationsApi();
  const resendMutation = useResendIntegrationWebhookLog(webhook.shortId);
  return (
    <CollapseCard
      keepCollapsedInDom
      defaultCollapsed
      spaceX={4}
      spaceY={2}
      variant="gray"
      renderCollapse={() => (
        <>
          {isLoading || !data ? (
            <RelativeSpinner />
          ) : data.length === 0 ? (
            <Stack space={2} orientation="horizontal" align="center">
              <IconFile color="elementPurple80" />
              <Text text={t('noLogsFound')} font="semibold14" color="textSecondary" />
            </Stack>
          ) : (
            <WebhookLogList
              logs={data}
              onResend={
                resendIntegrationWebhookLog
                  ? (log) => resendMutation.mutateAsync(log.shortId)
                  : undefined
              }
              getTravellerHref={getWebhookLogTravellerHref}
            />
          )}
        </>
      )}
    >
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Stack space={2} orientation="horizontal" align="center">
          <IconList color="elementPurple50" />
          <Text text={t('logs')} font="semibold14" />
        </Stack>
      </Stack>
    </CollapseCard>
  );
};

const locale = {
  en: {
    details: 'Details',
    webhookNotSubscribed: 'Not subscribed to a webhook',
    subscribe: 'Subscribe',
    requestValues: 'Request values',
    payloadValues: 'Payload values',
    responseValues: 'Response values',
    continueWhen: 'Continue when',
    unsubscribeWebhook: 'Unsubscribe webhook',
    noConditions: 'No conditions found',
    addConditions: 'Add conditions',
    conditionDescription: '{conjunction}{field} {comparator} {value}',
    continueIfDescription: 'Continue if\n{conditions}',
    filledOutInTheJourney: 'Value selected in journey builder',
    updateConditions: 'Update conditions',
    removeWebhook: 'Delete webhook',
    logs: 'Logs',
    noPayloadValues: 'No payload values found',
    editPayload: 'Edit payload values',
    payloadData: 'Payload Data',
    payloadQueryInfo:
      'The jq query will be executed against the webhook payload when it is received. Refer to https://jqlang.github.io/jq/ for syntax.',
    noLogsFound: 'No logs found',
  },
};
